/**
 * Phase 4 — client-side semver compare for the version UX surfaces. Mirrors
 * server/src/lib/environmentVersion.ts (parseSemver/compareSemver) EXACTLY so
 * the strip's "behind" and the promote-modal verdict can never disagree with
 * the server's assertFastForward enforcement (integer-triple order).
 */
export interface SemverTriple {
  major: number;
  minor: number;
  patch: number;
}

export function parseSemver(v: string): SemverTriple {
  const [a, b, c] = (v ?? '').split('+')[0].trim().split('.').map((n) => Number(n));
  return { major: a || 0, minor: b || 0, patch: c || 0 };
}

/** -1 if a<b, 0 if equal, 1 if a>b — integer compare on (major,minor,patch). */
export function compareSemver(a: SemverTriple, b: SemverTriple): number {
  if (a.major !== b.major) return a.major < b.major ? -1 : 1;
  if (a.minor !== b.minor) return a.minor < b.minor ? -1 : 1;
  if (a.patch !== b.patch) return a.patch < b.patch ? -1 : 1;
  return 0;
}

/**
 * The version a publish would mint next — a single patch bump off `current`.
 * (e.g. 1.0.5 -> 1.0.6, 1.0.0 -> 1.0.1). Reuses parseSemver so garbage inputs
 * coalesce to 0.0.0 -> 0.0.1 rather than throwing.
 */
export function predictNextVersion(current: string): string {
  const { major, minor, patch } = parseSemver(current);
  return `${major}.${minor}.${patch + 1}`;
}
